import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Drawer, Table, message } from 'antd';
import PropTypes from 'prop-types';
import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import UserService from './state/users.service';
import Permissions from '../auth/Permissions';

export default function SendWelcomeEmailsDrawer({ visible, onClose }) {
  const users = useSelector((state) => state.users.users);
  const settings = useSelector((state) => state.settings?.company?.settings);

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const filteredUsers = useMemo(() => {
    const eligibleUsers = users.filter((user) => {
      if (!user.active || !user.email) return false;
      const formattedName = Permissions.formatPosition(user.position);
      return Permissions.has(`USERS_${formattedName}`);
    });

    if (!searchText) return eligibleUsers;

    const lowerSearch = searchText.toLowerCase();
    return eligibleUsers.filter((user) => (
      user.name?.toLowerCase().includes(lowerSearch)
    ));
  }, [users, searchText]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedUserIds,
    onChange: (selectedKeys) => setSelectedUserIds(selectedKeys),
    getCheckboxProps: () => ({
      className: settings?.uxRedesignPt1 ? 'ontraccr-checkbox' : '',
    }),
  };

  const onHeaderRow = useCallback(() => {
    if (!settings?.uxRedesignPt1) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [settings]);

  const handleSend = async () => {
    if (selectedUserIds.length === 0) {
      message.warning('Please select at least one user');
      return;
    }

    setLoading(true);
    try {
      await UserService.sendWelcomeEmails(selectedUserIds);
      setSelectedUserIds([]);
      onClose();
    } catch (error) {
      message.error('Error sending welcome emails');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setSearchText('');
    onClose();
  };

  return (
    <Drawer
      title="Send Welcome Emails"
      placement="right"
      onClose={handleClose}
      visible={visible}
      width={600}
    >
      <div style={{ marginBottom: 16 }}>
        <p>
          Select users to send welcome emails to.
          Only active users with email addresses are shown.
        </p>
      </div>
      <OnTraccrTextInput
        placeholder="Search by name or email"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        allowClear
        style={{ marginBottom: 16 }}
      />
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredUsers}
        rowKey="id"
        size="small"
        pagination={false}
        onHeaderRow={onHeaderRow}
        scroll={{ y: 'calc(100vh - 350px)' }}
      />
      {selectedUserIds.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <p><strong>{selectedUserIds.length}</strong> user(s) selected</p>
        </div>
      )}
      <DrawerSubmitFooter
        onClose={handleClose}
        onSubmit={handleSend}
        loading={loading}
        canSubmit={selectedUserIds.length > 0}
        submitTitle="Send Emails"
      />
    </Drawer>
  );
}

SendWelcomeEmailsDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
